<template>
    <div class="course-banner" :style="bannerStyles">
        <div v-if="canEdit" class="edit-banner-btn">
            <b-button 
            variant="light" 
            size="sm" 
            @click="$emit('edit-banner')"
            v-b-tooltip.hover
            :title="$t('academy.edit_banner')">
            <b-icon-pencil></b-icon-pencil>
            {{ $t('academy.edit_banner_short') }}
        </b-button>
        </div>

        <div class="banner-overlay">
        <div class="banner-content">
            <!-- Información del curso -->
            <div class="course-info">
                    <div class="course-title-container">
                        <h1 v-if="!isEditingTitle" class="course-title" @click="startEditTitle">
                            {{ bannerData.courseName }}
                            <b-icon-pencil 
                                v-if="canEdit" 
                                class="edit-title-icon ml-3"
                                v-b-tooltip.hover
                                :title="$t('academy.edit_name')">
                            </b-icon-pencil>
                        </h1>
                        
                        <div v-else class="title-edit-container">
                            <div class="title-input-wrapper">
                                <b-form-input
                                    ref="titleInput"
                                    v-model="tempTitle"
                                    class="title-input"
                                    @keyup.enter="saveTitleChange"
                                    @keyup.esc="cancelTitleEdit"
                                    :placeholder="$t('academy.enter_new_name')+'...'"
                                    :state="tempTitle.trim().length >= 3 ? null : false"
                                />
                                <div class="input-counter" :class="{ 'counter-error': tempTitle.length < 3 }">
                                    {{ tempTitle.length }}/60
                                </div>
                            </div>
                            
                            <div class="title-edit-actions">
                                <b-button 
                                    size="sm" 
                                    variant="success" 
                                    @click="saveTitleChange"
                                    :disabled="tempTitle.trim().length < 3 || isSavingTitle"
                                    class="save-btn">
                                    <b-spinner v-if="isSavingTitle" small class="mr-1"></b-spinner>
                                    <b-icon-check v-else class="mr-1"></b-icon-check>
                                    {{ isSavingTitle ? $t('academy.saving') : $t('academy.save') }}
                                </b-button>
                                
                                <b-button 
                                    size="sm" 
                                    variant="outline-light" 
                                    @click="cancelTitleEdit"
                                    :disabled="isSavingTitle"
                                    class="cancel-btn">
                                    <b-icon-x class="mr-1"></b-icon-x>
                                    {{ this.$t('academy.cancel') }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                    
            <p class="course-description" v-if="bannerData.courseDescription">
                {{ bannerData.courseDescription }}
            </p>
            
            <!-- Metadatos del curso -->
            <div class="course-meta">
                <span class="course-institution" v-if="bannerData.institution">
                <i class="fas fa-university"></i>
                {{ bannerData.institution }}
                </span>
                
                <span class="course-modality" v-if="bannerData.modality">
                <i class="fas fa-tag"></i>
                {{ bannerData.modality === 'premium' ? 'Premium' : $t('academy.free') }}
                </span>
                
                <span class="course-modules" v-if="bannerData.modulesCount">
                <i class="fas fa-book"></i>
                {{ bannerData.modulesCount }}  {{ $t('academy.modules') }}
                </span>
            </div>
            </div>
            
            <!-- Logo de la institución (opcional) -->
            <div class="institution-logo" v-if="bannerData.institutionLogo">
            <img :src="bannerData.institutionLogo" :alt="bannerData.institution" />
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import { BIconPencil, BIconCheck, BIconX } from 'bootstrap-vue';

export default {
    name: 'CourseBanner',
    components: {
        BIconPencil,
        BIconCheck,
        BIconX
    },
    props: {
        bannerData: {
            type: Object,
            required: true
        },
        canEdit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isEditingTitle: false,
            tempTitle: '',
            isSavingTitle: false
        };
    },
    computed: {
        bannerStyles() {
            return {
                backgroundImage: this.bannerData.bannerImage 
                    ? `url(${this.bannerData.bannerImage})` 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundColor: this.bannerData.backgroundColor || '#667eea'
            };
        }
    },
    methods: {
        startEditTitle() {
            if (!this.canEdit || this.isSavingTitle) return;
            
            console.log('Iniciando edición del título');
            this.isEditingTitle = true;
            this.tempTitle = this.bannerData.courseName || '';
            
            this.$nextTick(() => {
                if (this.$refs.titleInput) {
                    this.$refs.titleInput.focus();
                    this.$refs.titleInput.select();
                }
            });
        },

        async saveTitleChange() {
            const newTitle = this.tempTitle.trim();
            
            if (!newTitle) {
                this.$bvToast.toast('El nombre del curso no puede estar vacío', {
                    title: 'Error de validación',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (newTitle === this.bannerData.courseName) {
                console.log('Sin cambios en el título, cancelando edición');
                this.cancelTitleEdit();
                return;
            }

            try {
                this.isSavingTitle = true;
                console.log('Guardando nuevo título:', newTitle);

                // ✅ EMITIR EVENTO PARA ACTUALIZAR EN COMPONENTE PADRE
                this.$emit('title-changed', {
                    courseId: this.bannerData.courseId,
                    newTitle: newTitle,
                    oldTitle: this.bannerData.courseName
                });

                this.isEditingTitle = false;

            } catch (error) {
                console.error('Error al guardar título:', error);
                this.$bvToast.toast('No se pudo guardar el nuevo nombre', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isSavingTitle = false;
            }
        },

        cancelTitleEdit() {
            console.log('Cancelando edición del título');
            this.isEditingTitle = false;
            this.tempTitle = '';
        }
    }
};
</script>

<style scoped>
.course-banner {
    height: 330px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 30px;
    margin-bottom: 20px;
    position: relative;
    overflow: hidden;
}

.edit-banner-btn {
    position: absolute;
    top: 15px;
    right: 15px;
    z-index: 10;
}

.banner-overlay {
position: absolute;
top: 0;
left: 0;
right: 0;
bottom: 0;
background: rgba(0, 0, 0, 0.65);
display: flex;
align-items: center;
padding: 20px;
}

.banner-content {
display: flex;
justify-content: space-between;
align-items: center;
width: 100%;
color: white;
}

.course-info {
    flex-grow: 1;
}

.course-title-container {
    position: relative;
    margin-bottom: 15px;
}

.course-title {
    font-size: 2.5rem;
    font-weight: bold;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: opacity 0.3s ease;
}

.course-title:hover {
    opacity: 0.9;
}

.edit-title-icon {
    font-size: 1.2rem;
    opacity: 0.7;
    transition: opacity 0.3s ease;
    cursor: pointer;
}

.edit-title-icon:hover {
    opacity: 1;
}

.title-edit-container {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 600px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.title-input-wrapper {
    position: relative;
}

.title-input {
    font-size: 1.8rem;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: #2c3e50;
    padding: 12px 16px;
    border-radius: 10px;
    flex-grow: 1;
    transition: all 0.3s ease;
}


.title-input:focus {
    background: white;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
    outline: none;
}

.title-input.is-invalid {
    border-color: #e74c3c;
    box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.2);
}

.input-counter {
    position: absolute;
    top: -8px;
    right: 12px;
    background: rgba(102, 126, 234, 0.9);
    color: white;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.input-counter.counter-error {
    background: #e74c3c;
}

/* Mejorar botones */
.title-edit-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}

.save-btn {
    background: linear-gradient(135deg, #667eea 0%, #705cc7 100%);
    border: none;
    color: white;
    font-weight: 500;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.save-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #5a6fd8 0%, #6b4190 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.save-btn:disabled {
    opacity: 0.6;
    background: linear-gradient(135deg, #a8b3f0 0%, #b19bc4 100%);
}

.cancel-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
    font-weight: 500;
    transition: all 0.3s ease;
}

.cancel-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
}

/* Responsive para el input */
@media (max-width: 768px) {
    .title-input {
        font-size: 1.4rem;
        padding: 10px 14px;
    }
    
    .title-edit-actions {
        flex-direction: column;
    }
    
    .title-edit-actions .btn {
        width: 100%;
    }
}

.title-edit-actions .btn {
    padding: 4px 8px;
    border-radius: 4px;
}

.course-description {
    font-size: 1.1rem;
    margin-bottom: 15px;
    opacity: 0.9;
    line-height: 1.4;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.course-meta {
display: flex;
gap: 20px;
flex-wrap: wrap;
}

.course-meta span {
    background: rgba(255, 255, 255, 0.2);
    padding: 5px 12px;
    border-radius: 15px;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 5px;
}

.institution-logo {
    flex-shrink: 0;
}

.institution-logo img {
    max-height: 60px;
    max-width: 120px;
    object-fit: contain;
}

@media (max-width: 768px) {
    .course-title {
        font-size: 1.8rem;
    }
    
    .title-input {
        font-size: 1.5rem;
    }
    
    .title-edit-container {
        flex-direction: column;
        align-items: stretch;
    }
    
    .title-edit-actions {
        justify-content: center;
        margin-top: 10px;
    }
}
</style>